// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Shared Button — the single source of truth for button styling.
// Before this existed, the app had 19 distinct padding values and 12 font
// sizes across ad-hoc inline-styled buttons. New code should use this;
// existing buttons migrate opportunistically.
//
//   <Button T={T} variant="primary" onClick={...}>Save</Button>
//   <Button T={T} variant="ghost" size="sm" icon={<Icon.X size={10}/>}>Cancel</Button>

import React, { useState } from "react";
import type { Theme } from "../lib/theme";
import { RADIUS, FONT } from "../lib/theme";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const SIZES: Record<ButtonSize, { padding: string; fontSize: number; minHeight: number; gap: number }> = {
  sm: { padding: "3px 10px",  fontSize: 10, minHeight: 22, gap: 5 },
  md: { padding: "6px 14px",  fontSize: 11, minHeight: 30, gap: 6 },
  // lg meets the 44px touch-target minimum — use for primary mobile/tablet actions
  lg: { padding: "10px 20px", fontSize: 13, minHeight: 44, gap: 8 },
};

export default function Button({
  T,
  variant = "secondary",
  size = "md",
  icon,
  children,
  onClick,
  disabled = false,
  title,
  type = "button",
  fullWidth = false,
  style,
}: {
  T: Theme;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  title?: string;
  type?: "button" | "submit";
  fullWidth?: boolean;
  style?: React.CSSProperties;
}) {
  const [hover, setHover] = useState(false);
  const s = SIZES[size];

  const palette = (() => {
    switch (variant) {
      case "primary":
        return {
          bg: hover ? T.borderAccent : T.borderAccent,
          color: T.textOnGold,
          border: `1px solid ${T.borderAccent}`,
          hoverFilter: "brightness(1.1)",
        };
      case "danger":
        return {
          bg: hover ? T.negative : `${T.negative}15`,
          color: hover ? "#fff" : T.negative,
          border: `1px solid ${T.negative}66`,
          hoverFilter: "none",
        };
      case "ghost":
        return {
          bg: hover ? T.bgHover : "transparent",
          color: hover ? T.textSecondary : T.textWeak,
          border: "1px solid transparent",
          hoverFilter: "none",
        };
      case "secondary":
      default:
        return {
          bg: hover ? `${T.borderAccent}15` : "transparent",
          color: hover ? T.borderAccent : T.textTertiary,
          border: `1px solid ${hover ? T.borderAccent : T.borderSecondary}`,
          hoverFilter: "none",
        };
    }
  })();

  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        padding: s.padding,
        minHeight: s.minHeight,
        fontSize: s.fontSize,
        fontWeight: 600,
        fontFamily: FONT.sans,
        borderRadius: RADIUS.sm,
        cursor: disabled ? "not-allowed" : "pointer",
        background: palette.bg,
        color: palette.color,
        border: palette.border,
        filter: hover && !disabled ? palette.hoverFilter : "none",
        opacity: disabled ? 0.45 : 1,
        transition: "all .15s ease-out",
        whiteSpace: "nowrap",
        width: fullWidth ? "100%" : undefined,
        boxSizing: "border-box",
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}
